/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React from "react"
import { Col, Row } from "@duxton-web/ui"
import { useTranslation } from "common/utils/i18n"
import { StarRating } from "components/common/StarRating"
import { DisplayRatingProp } from "./types"
import { fontStyle } from "./shared"

const DetailedRating: React.FC<DisplayRatingProp> = ({
  feedbackOverview,
  ratingCountFormatted,
}) => {
  const renderRatingBox = (num: string) => {
    const colorHex = Number(num) >= 4 ? "#005339" : "#f09800"
    return (
      <div
        css={css({
          padding: 8,
          width: 40,
          height: 40,
          borderRadius: 8,
          background: colorHex,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        <p
          css={css({
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: 16,
            lineHeight: "24px",
            color: "#ffffff",
            margin: 0,
          })}
        >
          {num}
        </p>
      </div>
    )
  }

  const renderCategoryRating = (category: string, num: string, i: number) => (
    <Col
      span={8}
      key={`rating-category-${category}-${i}`}
      data-testid={`rating-category-${category}`}
      css={css({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginTop: i >= 3 ? 8 : 0,
      })}
    >
      {renderRatingBox(num)}
      <p
        css={css(fontStyle, {
          marginTop: 8,
        })}
      >
        {category}
      </p>
    </Col>
  )

  return (
    <Row
      align="middle"
      css={css({
        marginTop: 12,
      })}
    >
      <Col
        span={8}
        css={css({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          marginRight: 8,
        })}
      >
        <p
          css={css({
            fontStyle: "normal",
            fontWeight: 500,
            fontSize: 32,
            lineHeight: "40px",
            margin: 0,
          })}
        >
          {feedbackOverview?.aggregatedRatingScore
            ? Math.round(feedbackOverview?.aggregatedRatingScore * 10) / 10
            : 0}
        </p>
        <StarRating rating={feedbackOverview?.aggregatedRatingScore || 0} />
        <p css={fontStyle}>
          {useTranslation(`merchant.reviews.card.title`, {
            "<x>": ratingCountFormatted.toString(),
          })}
        </p>
      </Col>
      <Col span={15}>
        <Row>
          {feedbackOverview?.componentRating?.ratings?.map((rating, i) => {
            return renderCategoryRating(rating.itemLabel, rating.rating, i)
          })}
        </Row>
      </Col>
    </Row>
  )
}

export default DetailedRating
